import React, { FC, ReactNode, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import Utils from '../../shared/utils';
import { CAMPAIGN_PRODUCTS, isFeatureEnabled } from '../../shared/utils/togglz';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import ProductCover from '../../shared/common/ProductCover';
import SelectButton from '../../shared/common/SelectButton';
import { PreviewButton } from '../../shared/preview/PreviewButton';
import ProductStatistics from '../../shared/product/ProductStatistics';
import ProductSeries from '../../shared/product/ProductSeries';
import ProductAuthors from '../../shared/product/ProductAuthors';
import ProductNarrators from '../../shared/product/ProductNarrators';
import ProductGenres from '../../shared/product/ProductGenres';
import ProductAge from '../../shared/product/ProductAge';
import { ProductFormat } from '../../shared/product/ProductFormat';
import ProductPublisher from '../../shared/product/ProductPublisher';
import { ProductReleaseDate } from '../../shared/product/ProductReleaseDate';
import ProductUsageConditions from '../../shared/product/ProductUsageConditions';
import ProductLanguage from '../../shared/product/ProductLanguage';
import { concreteLoanFormatNameToObject } from '../../store/loanFormat/actions';
import { ListBadges } from '../../shared/common/BadgesV2';
import { CenteredListBadges } from '../../shared/common/BadgesV2/CenteredBadgeContainer';
import CampaignUsageBadge from '../../yourborrowbox/marketing/campaigns/CampaignUsageBadge';
import { AddProduct } from '../../shared/common/AddProduct/AddProduct';
import { ProductListItemProps } from '../../shared/common/ProductList';
import { log } from '../../shared/utils/log';
import MetadataUtils from '../../shared/utils/utils-metadata';
import { Article, Price, SubscriptionPeriodType } from '../../../models/Article';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { MATERIAL_ICON_SETTINGS } from '../../shared/common/Button/Icon';
import { ProductMetaData } from '../../../models/Product';
import { CopyDetailsPopup } from '../../yourborrowbox/collection/copyDetailsPopup/CopyDetailsPopup';
import { useTranslation } from 'react-i18next';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { hasProductSelection } from '../../shared/product-selection/ProductSelectionUtils';
import { ProductSelection } from '../../shared/product-selection/ProductSelectionTypes';

const StCampaignUsageBadge = styled(CampaignUsageBadge)`
    width: 136px;
`;

type Props = ProductListItemProps & {
    readonly actionButtons: ReactNode;
    readonly showSelectionLists?: boolean;
    readonly showSelectedPriceOnly?: boolean;
    readonly allowSelection?: boolean;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
    readonly onSelectPrice: (price: Price, subscriptionPeriodType?: SubscriptionPeriodType) => Promise<void>;
    readonly isBuyingOptionEnabled?: boolean;
    readonly shopLanguage: string | undefined;
    readonly productSelection: ProductSelection;

    /**
     * This is a workaround to enable the ProductSelection for the shop section.
     * Since the ProductTile setup needs its own refactoring, this workaround is ok.
     * But please remove it (build a solution that does not need it), when refactoring the ProductTile setup.
     */
    readonly useOnlyProductIdForSelectButtonCheck?: boolean;
};

const CatalogueListItem: FC<Props> = props => {
    const {
        product,
        loanFormat,
        productSelection,
        onProductImageClick,
        onSelectProduct,
        onAddPrice,
        onSelectPrice,
        showSelectedPriceOnly,
        allowSelection,
        actionButtons,
        isBuyingOptionEnabled = true,
        useOnlyProductIdForSelectButtonCheck = false,
        isProductSelectable
    } = props;
    const { t } = useTranslation();
    const [isShowDetails, setIsShowDetails] = useState(false);
    const showDetails = useCallback(() => setIsShowDetails(true), []);
    const hideDetails = useCallback(() => setIsShowDetails(false), []);
    const [selectedProduct, setSelectedProduct] = useState<(ProductMetaData & Article) | null>(null);

    const handleSelectPrice = useCallback(async () => {
        log.warning('tried to select a price within the catalogue');
    }, []);

    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;
    const isCampaignsEnabled = isFeatureEnabled(CAMPAIGN_PRODUCTS);
    const productLoanFormat = concreteLoanFormatNameToObject(product.loanFormat);
    const productLoanFormatKey = productLoanFormat?.key;
    const productLoanFormatSingularName = productLoanFormat?.singularName;
    const packshotClasses = classNames('product-list-cell', 'packshot', 'catalogue', isShowDetails && 'over');

    const productLink = hasProductSelection(productSelection) ? '#' : `/products/${product.id}`;

    const packshot = (
        <div className={packshotClasses}>
            <Link
                to={productLink}
                onClick={e => {
                    e.preventDefault();
                    onProductImageClick();
                }}
            >
                <ProductCover
                    onMouseEnter={showDetails}
                    onMouseLeave={hideDetails}
                    minSizeInPx={288}
                    product={product}
                />
            </Link>
            <SelectButton
                productOrId={useOnlyProductIdForSelectButtonCheck ? product.id : product}
                productSelection={productSelection}
                onClick={onSelectProduct}
                isProductSelectable={isProductSelectable}
            />
            <PreviewButton product={product} />
        </div>
    );

    // === TITLE, AUTHOR, ETC. ======================================================================

    let combinedTitle = product.title + (product.subTitle ? ` ${product.subTitle}` : '');

    const title = (
        <div className="tc tcw-25">
            <ProductSeries {...props} loanFormatKey={loanFormat} />
            <p title={combinedTitle} className="label">
                <Link to={productLink}>
                    <strong>{Utils.truncate(combinedTitle)}</strong>
                </Link>
            </p>
            <ProductAuthors {...props} loanFormatKey={loanFormat} />
            <ProductNarrators {...props} loanFormatKey={productLoanFormatKey} />
        </div>
    );

    // === FORMAT, GENRE, AGE ======================================================================

    //TODO: Remove feature toggle and add the Product Language Component for real
    const productLanguage = (
        <ProductLanguage
            product={product}
            loanFormatKey={loanFormat}
            label={product.language!}
            shopLanguage={props.shopLanguage}
        />
    );

    const info = (
        <div className="tc tcw-20">
            <ProductFormat {...props} loanFormatKey={productLoanFormatKey!} />
            <ProductGenres {...props} loanFormatKey={loanFormat} />
            <ProductAge {...props} loanFormatKey={loanFormat} />
            {productLanguage}
        </div>
    );

    // === ISBN, PUBLISHER, ETC. ======================================================================

    let campaignUsageBadge: ReactNode = null;
    if (isCampaignsEnabled && isCampaignProduct) {
        const isPreOrder = MetadataUtils.isProductPreOrderable(product);
        campaignUsageBadge = (
            <StCampaignUsageBadge
                loanFormat={productLoanFormatSingularName!}
                isSimultaneousAccess={product.simultaneousAccess}
                usageLimit={product.price?.usageLimit}
                campaignTerm={product.price?.termLimit}
                releaseDate={product.releaseDate}
                isPreOrder={isPreOrder}
            />
        );
    }

    const isbn = (
        <div className="tc tcw-25">
            <p>{product.isbn13}</p>
            <ProductReleaseDate {...props} />
            <ProductPublisher {...props} loanFormatKey={loanFormat} />
            <ProductUsageConditions product={product} loanFormatKey={loanFormat} shopLanguage={props.shopLanguage} />
            {campaignUsageBadge}
        </div>
    );

    // === ACTIONS / BUTTONS ======================================================================

    const actions = (
        <div className="tc tcw-20 actions">
            {actionButtons || null}
            <>
                <AddProduct
                    product={product}
                    /*
                     * TODO [muc] <CatalogueListItem> is used directly within the shop and also by
                     *  <SelectionlistListItem> as well as <CartListItem>, so the handleAddPrice is meant for the shop
                     *  only and can thus be overwritten by the wrapping component. In order to clean up this mess, we
                     *  should transform this component to a more generic <ListItem> component and add a new
                     *  <CatalogueListItem> wrapper component analogously to <SelectionlistListItem> and <CartListItem>.
                     */
                    onAddPrice={onAddPrice}
                    onSelectPrice={onSelectPrice ?? handleSelectPrice}
                    showSelectedPriceOnly={showSelectedPriceOnly ?? false}
                    allowSelection={allowSelection ?? true}
                />
                {selectedProduct ? (
                    <CopyDetailsPopup
                        isBuyingOptionEnabled={isBuyingOptionEnabled}
                        onClosePopup={() => setSelectedProduct(null)}
                        onAddPrice={onAddPrice}
                        product={selectedProduct}
                        shopLanguage={props.shopLanguage}
                    />
                ) : null}
                {product?.stockDetails ? (
                    <Button
                        text={t('manage')}
                        onClick={() => setSelectedProduct(product)}
                        icon={MATERIAL_ICON_SETTINGS}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.NEUTRAL}
                        fullWidth={true}
                        className="my-3"
                    />
                ) : null}
            </>
        </div>
    );

    return (
        <div className="product-list-row">
            {packshot}
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        {title}
                        {info}
                        {isbn}
                        {actions}
                    </div>
                </div>
                <div className="product-list-bottom">
                    <ProductStatistics
                        product={product}
                        additionalClassNames="fl-left"
                        loanFormatKey={productLoanFormatKey!}
                    />
                    {/*//TODO: CENTERING*/}
                    <CenteredListBadges>
                        <ListBadges
                            product={product}
                            loanFormatKey={loanFormat ?? undefined}
                            showAll={isShowDetails}
                            shopLanguage={props.shopLanguage}
                        />
                    </CenteredListBadges>
                </div>
            </div>
        </div>
    );
};

export default CatalogueListItem;
